import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { publicApi } from "../../api";
import SEO from "../../components/SEO";
import {
  Star,
  Check,
  X,
  ExternalLink,
  ArrowLeft,
  Zap,
  ChevronRight,
  DollarSign,
  Users,
  Globe,
  HelpCircle,
} from "lucide-react";
import ReactMarkdown from "react-markdown";

const ProductReview = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await publicApi.getProductBySlug(slug);
        setData(res.data);
      } catch (error) {
        console.error("Failed to load product:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const handleAffiliateClick = async () => {
    if (!data?.product) return;
    try {
      await publicApi.trackClick({
        product_id: data.product.id,
        niche_id: data.product.niche_id,
        source_page: "product_review",
      });
    } catch (e) {
      console.error("Failed to track click");
    }
    window.open(data.product.affiliate_link, "_blank");
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Star key={i} size={20} className="text-yellow-400 fill-yellow-400" />
      );
    }
    if (hasHalf) {
      stars.push(
        <Star key="half" size={20} className="text-yellow-400 fill-yellow-400/50" />
      );
    }
    for (let i = stars.length; i < 5; i++) {
      stars.push(
        <Star key={`empty-${i}`} size={20} className="text-slate-300" />
      );
    }
    return stars;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="product-loading">
        <div className="animate-pulse text-slate-500">Loading review...</div>
      </div>
    );
  }

  if (!data?.product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-slate-900">Product not found</h1>
        <Link to="/" className="text-blue-600 hover:underline mt-4 inline-block">
          Go back home
        </Link>
      </div>
    );
  }

  const { product, related_products, niche } = data;

  return (
    <div data-testid="product-review-page">
      <SEO
        title={`${product.name} Review ${new Date().getFullYear()} - Pros, Cons & Pricing`}
        description={`${product.name} review: ${product.tagline}. Rating: ${product.rating}/5. Starting at ${product.starting_price || 'Contact for pricing'}. Read our in-depth analysis.`}
        url={`/products/${product.slug}`}
        keywords={`${product.name} review, ${product.name} pricing, ${product.name} pros cons, ${niche?.name || 'software'} comparison`}
        product={{
          name: product.name,
          price: product.starting_price,
          rating: product.rating,
          ratingCount: product.rating_count,
        }}
      />
      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center gap-2 text-sm text-slate-600">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <ChevronRight size={14} />
            {niche && (
              <>
                <Link to={`/${niche.slug}`} className="hover:text-blue-600">
                  {niche.name}
                </Link>
                <ChevronRight size={14} />
              </>
            )}
            <span className="text-slate-900 font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Info */}
            <div className="lg:col-span-2">
              <div className="flex items-start gap-6">
                {product.logo_url ? (
                  <img
                    src={product.logo_url}
                    alt={product.name}
                    className="w-20 h-20 rounded-xl object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 text-3xl font-bold">
                    {product.name.charAt(0)}
                  </div>
                )}
                <div className="flex-1">
                  <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-serif">
                    {product.name} Review
                  </h1>
                  <p className="text-xl text-slate-600 mt-2">{product.tagline}</p>
                  <div className="flex items-center gap-4 mt-4">
                    <div className="flex items-center gap-1">
                      {renderStars(product.rating)}
                      <span className="ml-2 text-lg font-semibold text-slate-900">
                        {product.rating.toFixed(1)}/5
                      </span>
                    </div>
                    {product.rating_count > 0 && (
                      <span className="text-slate-500">
                        ({product.rating_count.toLocaleString()} reviews)
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-lg text-slate-600 mt-8 leading-relaxed">
                {product.description}
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <DollarSign size={14} />
                    Starting Price
                  </div>
                  <p className="font-bold text-slate-900 text-lg">
                    {product.starting_price || "Contact"}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <Users size={14} />
                    Best For
                  </div>
                  <p className="font-bold text-slate-900 text-lg">
                    {product.best_for}
                  </p>
                </div>
                {product.primary_metric && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                      <Zap size={14} />
                      {product.primary_metric}
                    </div>
                    <p className="font-bold text-slate-900 text-lg">
                      {product.primary_metric_value}
                    </p>
                  </div>
                )}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <Globe size={14} />
                    Free Tier
                  </div>
                  <p className="font-bold text-slate-900 text-lg">
                    {product.has_free_tier ? "Yes" : "No"}
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-slate-50 rounded-xl p-6 border border-slate-200">
                <div className="text-center mb-6">
                  <span className="text-sm text-slate-500">Starting at</span>
                  <p className="text-4xl font-bold text-slate-900 mt-1">
                    {product.starting_price || "Contact"}
                  </p>
                  {product.has_free_tier && (
                    <span className="inline-block mt-2 text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full">
                      Free tier available
                    </span>
                  )}
                </div>
                <button
                  onClick={handleAffiliateClick}
                  className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  data-testid="main-cta-btn"
                >
                  Visit {product.name}
                  <ExternalLink size={18} />
                </button>
                <a
                  href={product.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full mt-3 py-3 border border-slate-300 text-slate-700 font-medium rounded-lg transition-colors hover:bg-white text-center"
                >
                  View Official Site
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Verdict */}
      {product.ai_verdict && (
        <section className="bg-blue-50 border-b border-blue-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Zap size={24} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 font-serif">Quick Verdict</h2>
                <p className="text-slate-700 mt-2 text-lg leading-relaxed">
                  {product.ai_verdict}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Pros & Cons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {product.pros?.length > 0 && (
                  <div>
                    <h3 className="text-xl font-bold text-green-700 mb-4 font-serif flex items-center gap-2">
                      <Check className="text-green-500" /> Pros
                    </h3>
                    <ul className="space-y-3">
                      {product.pros.map((pro, i) => (
                        <li key={i} className="flex items-start gap-3 text-slate-700">
                          <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                          <span>{pro}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {product.cons?.length > 0 && (
                  <div>
                    <h3 className="text-xl font-bold text-red-700 mb-4 font-serif flex items-center gap-2">
                      <X className="text-red-500" /> Cons
                    </h3>
                    <ul className="space-y-3">
                      {product.cons.map((con, i) => (
                        <li key={i} className="flex items-start gap-3 text-slate-700">
                          <X size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                          <span>{con}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* AI Review */}
              {product.ai_review && (
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-6 font-serif">
                    In-Depth Review
                  </h2>
                  <div className="prose prose-slate max-w-none">
                    <ReactMarkdown>{product.ai_review}</ReactMarkdown>
                  </div>
                </div>
              )}

              {/* Key Features */}
              {(product.unique_feature_1 || product.unique_feature_2) && (
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-6 font-serif">
                    Key Features
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {product.primary_metric && (
                      <div className="p-5 bg-slate-50 rounded-xl border border-slate-200">
                        <span className="text-sm text-slate-500 uppercase tracking-wider">
                          {product.primary_metric}
                        </span>
                        <p className="font-bold text-slate-900 text-xl mt-2">
                          {product.primary_metric_value}
                        </p>
                      </div>
                    )}
                    {product.unique_feature_1 && (
                      <div className="p-5 bg-slate-50 rounded-xl border border-slate-200">
                        <span className="text-sm text-slate-500 uppercase tracking-wider">
                          {product.unique_feature_1}
                        </span>
                        <p className="font-bold text-slate-900 text-xl mt-2">
                          {product.unique_feature_1_value}
                        </p>
                      </div>
                    )}
                    {product.unique_feature_2 && (
                      <div className="p-5 bg-slate-50 rounded-xl border border-slate-200">
                        <span className="text-sm text-slate-500 uppercase tracking-wider">
                          {product.unique_feature_2}
                        </span>
                        <p className="font-bold text-slate-900 text-xl mt-2">
                          {product.unique_feature_2_value}
                        </p>
                      </div>
                    )}
                    {product.integrations?.length > 0 && (
                      <div className="p-5 bg-slate-50 rounded-xl border border-slate-200">
                        <span className="text-sm text-slate-500 uppercase tracking-wider">
                          Integrations
                        </span>
                        <p className="font-bold text-slate-900 text-xl mt-2">
                          {product.integrations.join(", ")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* FAQ */}
              {product.ai_faq?.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-6 font-serif flex items-center gap-2">
                    <HelpCircle size={24} /> Frequently Asked Questions
                  </h2>
                  <div className="space-y-4">
                    {product.ai_faq.map((faq, i) => (
                      <div key={i} className="p-5 bg-slate-50 rounded-xl border border-slate-200">
                        <h4 className="font-semibold text-slate-900 text-lg">
                          {faq.question}
                        </h4>
                        <p className="text-slate-600 mt-2 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Sidebar - Related Products */}
            <div className="lg:col-span-1">
              {related_products?.length > 0 && (
                <div className="sticky top-24">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 font-serif">
                    Alternatives to Consider
                  </h3>
                  <div className="space-y-4">
                    {related_products.map((related) => (
                      <Link
                        key={related.id}
                        to={`/products/${related.slug}`}
                        className="block p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all"
                        data-testid={`related-${related.slug}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                            {related.name.charAt(0)}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-slate-900">
                              {related.name}
                            </h4>
                            <div className="flex items-center gap-2 mt-1">
                              <Star size={12} className="text-yellow-400 fill-yellow-400" />
                              <span className="text-sm text-slate-600">
                                {related.rating.toFixed(1)}
                              </span>
                              <span className="text-slate-400">•</span>
                              <span className="text-sm text-slate-600">
                                {related.starting_price || "Contact"}
                              </span>
                            </div>
                          </div>
                          <ChevronRight size={16} className="text-slate-400" />
                        </div>
                      </Link>
                    ))}
                  </div>
                  {niche && (
                    <Link
                      to={`/${niche.slug}`}
                      className="block mt-4 text-center text-blue-600 hover:underline font-medium"
                    >
                      View All {niche.name} →
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-slate-900 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white font-serif">
            Ready to try {product.name}?
          </h2>
          <p className="text-slate-300 mt-2">
            {product.has_free_tier
              ? "Start with their free tier and upgrade when you're ready."
              : "Get started today with their professional plans."}
          </p>
          <button
            onClick={handleAffiliateClick}
            className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors mt-6"
            data-testid="bottom-cta-btn"
          >
            Visit {product.name}
            <ExternalLink size={18} />
          </button>
        </div>
      </section>
    </div>
  );
};

export default ProductReview;
